import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import type { Produit } from '../produits/types.ts';

/**
 * Lignes d'un document de stock : réception fournisseur ou envoi entre
 * boutiques.
 *
 * On cherche un produit, on l'ajoute, on saisit la quantité — et le coût
 * d'achat pour une réception. Le stock affiché est celui de la boutique
 * choisie : il dit à l'expéditeur ce qu'il peut raisonnablement envoyer.
 */

export interface LigneSaisie {
  produitId: string;
  designation: string;
  reference: string;
  quantite: string;
  /** Réception seulement : coût d'achat unitaire, en FCFA entiers. */
  cout: string;
}

/** Entier strictement positif, tel que saisi. */
export function entierPositif(valeur: string): number | null {
  return /^\d+$/.test(valeur) && Number(valeur) > 0 ? Number(valeur) : null;
}

/** Entier positif ou nul, tel que saisi. */
export function entierOuZero(valeur: string): number | null {
  return /^\d+$/.test(valeur) ? Number(valeur) : null;
}

export function LignesArticles({
  boutiqueId,
  lignes,
  onChange,
  avecCout,
}: {
  boutiqueId: string | null;
  lignes: LigneSaisie[];
  onChange: (lignes: LigneSaisie[]) => void;
  avecCout: boolean;
}): ReactNode {
  const [recherche, setRecherche] = useState('');

  // Le canal DETAIL sert de référence : on déplace un PRODUIT, pas un tarif.
  // `distribution_stricte` écarte ce que CETTE boutique ne vend pas
  // (RG-196) : sans ça, la suggestion acceptait une ligne que le serveur
  // refusait ensuite à l'enregistrement du document, une fois déjà saisie.
  const { data: trouves } = useQuery({
    queryKey: ['produits-stock', boutiqueId, recherche],
    enabled: boutiqueId !== null && recherche.trim().length >= 2,
    queryFn: () =>
      appeler<{ donnees: Produit[] }>(
        `/produits?canal=DETAIL&boutique_id=${boutiqueId ?? ''}&distribution_stricte=1` +
          `&q=${encodeURIComponent(recherche.trim())}`,
      ),
    select: (reponse) => reponse.donnees.slice(0, 8),
  });

  function ajouter(produit: Produit): void {
    if (lignes.some((ligne) => ligne.produitId === produit.id)) {
      return; // Une ligne par produit : deux lignes rendraient le document ambigu.
    }
    onChange([
      ...lignes,
      {
        produitId: produit.id,
        designation: produit.designation,
        reference: produit.reference,
        quantite: '',
        cout: '',
      },
    ]);
    setRecherche('');
  }

  function modifier(index: number, champ: 'quantite' | 'cout', valeur: string): void {
    onChange(
      lignes.map((ligne, position) =>
        position === index ? { ...ligne, [champ]: valeur.replace(/\D/g, '') } : ligne,
      ),
    );
  }

  return (
    <>
      <div className="champ">
        <label htmlFor="recherche-produit">Ajouter un produit</label>
        <input
          id="recherche-produit"
          placeholder={boutiqueId === null ? 'Choisissez d’abord la boutique' : 'Nom ou référence (2 lettres au moins)'}
          disabled={boutiqueId === null}
          value={recherche}
          onChange={(evenement) => {
            setRecherche(evenement.target.value);
          }}
        />
      </div>

      {(trouves ?? []).length > 0 && (
        <ul className="liste-choix" aria-label="Produits trouvés">
          {(trouves ?? []).map((produit) => (
            <li key={produit.id}>
              <button
                type="button"
                className="bouton--discret"
                onClick={() => {
                  ajouter(produit);
                }}
              >
                + {produit.designation}{' '}
                <span className="mono">
                  ({produit.reference} — stock {produit.stockDisponible})
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {lignes.length > 0 && (
        <div className="tableau-defilant">
          <table>
            <thead>
              <tr>
                <th>Produit</th>
                <th>Quantité</th>
                {avecCout && <th>Coût unitaire (FCFA)</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {lignes.map((ligne, index) => (
                <tr key={ligne.produitId}>
                  <td>
                    {ligne.designation} <span className="mono">{ligne.reference}</span>
                  </td>
                  <td>
                    <input
                      aria-label={`Quantité — ${ligne.designation}`}
                      inputMode="numeric"
                      value={ligne.quantite}
                      onChange={(evenement) => {
                        modifier(index, 'quantite', evenement.target.value);
                      }}
                    />
                  </td>
                  {avecCout && (
                    <td>
                      <input
                        aria-label={`Coût unitaire — ${ligne.designation}`}
                        inputMode="numeric"
                        value={ligne.cout}
                        onChange={(evenement) => {
                          modifier(index, 'cout', evenement.target.value);
                        }}
                      />
                    </td>
                  )}
                  <td>
                    <button
                      type="button"
                      className="bouton--discret"
                      onClick={() => {
                        onChange(lignes.filter((_, position) => position !== index));
                      }}
                    >
                      Retirer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
